import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import article from "./Content";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";
import './PagesCss/StockProfitCalculator.css';

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
); 

const StockProfitCalculator = () => {
  const content = article.find((item) => item.id === 1);
  const [mode, setMode] = useState("trader");
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [riskLevel, setRiskLevel] = useState("medium");
  const [notifications, setNotifications] = useState([]);

  const [inputs, setInputs] = useState({
    buyPrice: "50.00",
    sellPrice: "65.00",
    shares: "100",
    buyFee: "5.00",
    sellFee: "5.00",
    taxes: "15.00",
    dividend: "0.50",
    dividendFrequency: "quarterly",
    slippage: "2.00",
    otherFees: "3.00",
    leverage: "1",
    holdingPeriod: "30",
    currency: "1",
    reinvest: "25.00",
    initialInvestment: "5000",
    monthlyContribution: "200",
    timePeriod: "24",
    annualReturn: "8",
    riskAmount: "100",
    stopLoss: "45.00",
    takeProfit: "70.00"
  });

  const [result, setResult] = useState(null);
  const [calculationHistory, setCalculationHistory] = useState([]);
  const chartRef = useRef();

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator") {
      calculateProfit();
    }
  }, [inputs, mode, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const calculateProfit = () => {
    const {
      buyPrice, sellPrice, shares, buyFee, sellFee, taxes,
      dividend, slippage, otherFees, leverage, currency, reinvest,
      dividendFrequency, stopLoss, takeProfit
    } = inputs;

    if (!buyPrice || !sellPrice || !shares) {
      return;
    }

    const bp = parseFloat(buyPrice);
    const sp = parseFloat(sellPrice);
    const sh = parseFloat(shares);
    const lev = parseFloat(leverage) || 1;
    const curr = parseFloat(currency) || 1;

    const totalInvestment = (bp * sh * lev);
    const totalBuyCost = totalInvestment + parseFloat(buyFee || 0) + parseFloat(otherFees || 0);
    const totalSellValue = (sp * sh * lev) - parseFloat(sellFee || 0) - parseFloat(taxes || 0) - parseFloat(slippage || 0);
    
    // Enhanced dividend calculation
    let dividendMultiplier = 1;
    switch(dividendFrequency) {
      case "monthly": dividendMultiplier = 12; break;
      case "quarterly": dividendMultiplier = 4; break;
      case "semi-annual": dividendMultiplier = 2; break;
      case "annual": dividendMultiplier = 1; break;
    }
    const netDividend = (parseFloat(dividend || 0) * sh * dividendMultiplier) + parseFloat(reinvest || 0);
    
    const grossProfit = totalSellValue - totalInvestment;
    const netProfit = totalSellValue - totalBuyCost + netDividend;
    const percentGain = (netProfit / totalBuyCost) * 100;
    const roi = (netProfit / totalInvestment) * 100;

    // Risk management calculations
    const stopLossPrice = parseFloat(stopLoss || 0);
    const takeProfitPrice = parseFloat(takeProfit || 0);
    const potentialLoss = stopLossPrice ? ((stopLossPrice - bp) * sh * lev) : 0;
    const potentialGain = takeProfitPrice ? ((takeProfitPrice - bp) * sh * lev) : 0;
    const riskRewardRatio = potentialLoss !== 0 ? Math.abs(potentialGain / potentialLoss) : 0;

    const resultData = {
      totalInvestment: totalInvestment * curr,
      totalBuyCost: totalBuyCost * curr,
      totalSellValue: totalSellValue * curr,
      netDividend: netDividend * curr,
      grossProfit: grossProfit * curr,
      netProfit: netProfit * curr,
      percentGain,
      roi,
      leverageUsed: lev,
      breakEvenPrice: (totalBuyCost / (sh * lev)).toFixed(2),
      potentialLoss,
      potentialGain,
      riskRewardRatio: riskRewardRatio.toFixed(2),
      positionSize: totalInvestment * curr
    };

    setResult(resultData);
  };

  const calculateProjection = () => {
    const { initialInvestment, monthlyContribution, timePeriod, annualReturn } = inputs;
    
    if (!initialInvestment) return;

    const initial = parseFloat(initialInvestment);
    const monthly = parseFloat(monthlyContribution || 0);
    const months = parseInt(timePeriod);
    const annualRate = parseFloat(annualReturn || 8) / 100;
    const monthlyRate = annualRate / 12;

    const projectionData = [];
    let totalValue = initial;

    for (let i = 0; i <= months; i++) {
      if (i > 0) {
        totalValue = totalValue * (1 + monthlyRate) + monthly;
      }
      projectionData.push({
        month: i,
        value: totalValue,
        contributions: initial + (monthly * i)
      });
    }

    return projectionData;
  };

  const calculateRiskMetrics = () => {
    const { riskAmount, buyPrice, stopLoss } = inputs;
    
    if (!riskAmount || !buyPrice || !stopLoss) return null;

    const risk = parseFloat(riskAmount);
    const bp = parseFloat(buyPrice);
    const sl = parseFloat(stopLoss);
    
    const riskPerShare = bp - sl;
    const positionSize = risk / riskPerShare;
    const totalInvestment = positionSize * bp;

    return {
      riskPerShare,
      positionSize: Math.floor(positionSize),
      totalInvestment,
      riskPercentage: (risk / totalInvestment * 100).toFixed(2)
    };
  };

  const resetCalculator = () => {
    setInputs({
      buyPrice: "50.00",
      sellPrice: "65.00",
      shares: "100",
      buyFee: "5.00",
      sellFee: "5.00",
      taxes: "15.00",
      dividend: "0.50",
      dividendFrequency: "quarterly",
      slippage: "2.00",
      otherFees: "3.00",
      leverage: "1",
      holdingPeriod: "30",
      currency: "1",
      reinvest: "25.00",
      initialInvestment: "5000",
      monthlyContribution: "200",
      timePeriod: "24",
      annualReturn: "8",
      riskAmount: "100",
      stopLoss: "45.00",
      takeProfit: "70.00"
    });
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!result) return;
    
    const csvContent = [
      "Metric,Value",
      `Total Investment,${result.totalInvestment.toFixed(2)}`,
      `Total Cost,${result.totalBuyCost.toFixed(2)}`,
      `Sale Value,${result.totalSellValue.toFixed(2)}`,
      `Net Profit,${result.netProfit.toFixed(2)}`,
      `Percentage Gain,${result.percentGain.toFixed(2)}%`,
      `ROI,${result.roi.toFixed(2)}%`,
      `Break-even Price,${result.breakEvenPrice}`,
      `Risk/Reward Ratio,${result.riskRewardRatio}`,
      `Position Size,${result.positionSize.toFixed(2)}`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'profit-calculation.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Data exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!result) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      ...result
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Calculation saved to history", "success");
  };

  const loadCalculation = (calc) => {
    setInputs(calc.inputs);
    setResult(calc.result);
    setShowSaved(false);
    addNotification("Calculation loaded successfully", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      conservative: { leverage: "1", stopLoss: "10", takeProfit: "20" },
      moderate: { leverage: "2", stopLoss: "15", takeProfit: "30" },
      aggressive: { leverage: "3", stopLoss: "20", takeProfit: "50" }
    };
    
    setInputs(prev => ({ ...prev, ...presets[preset] }));
    addNotification(`${preset} preset applied`, "info");
  };

  // Chart Data
  const profitChartData = result ? {
    labels: ["Initial Investment", "Net Profit", "Fees & Taxes", "Dividends"],
    datasets: [{
      data: [
        result.totalInvestment, 
        Math.max(result.netProfit, 0), 
        Math.abs(Math.min(result.netProfit - result.totalInvestment, 0)),
        result.netDividend
      ],
      backgroundColor: ["#19692C", "#28A745", "#FF6B6B", "#FFD93D"],
      borderWidth: 2,
      hoverOffset: 15
    }],
  } : null;

  const projectionData = calculateProjection();
  const projectionChartData = projectionData ? {
    labels: projectionData.map(d => `Month ${d.month}`),
    datasets: [
      {
        label: 'Portfolio Value',
        data: projectionData.map(d => d.value),
        borderColor: "#19692C",
        backgroundColor: "rgba(25, 105, 44, 0.1)",
        fill: true,
        tension: 0.4,
        pointBackgroundColor: "#19692C",
        pointBorderColor: "#FFFFFF",
        pointBorderWidth: 2
      },
      {
        label: 'Total Contributions',
        data: projectionData.map(d => d.contributions),
        borderColor: "#FFD93D",
        backgroundColor: "rgba(255, 217, 61, 0.1)",
        fill: false,
        borderDash: [5, 5],
        tension: 0.4
      }
    ]
  } : null;

  const riskMetrics = calculateRiskMetrics();

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Stock Profit Calculator</h1>
        </div>
        <p>Real-time profit calculations, investment projections, and comprehensive analysis</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      {/* Saved Calculations Modal */}
      {showSaved && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Saved Calculations</h3>
              <button className="close-btn" onClick={() => setShowSaved(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="saved-list">
              {savedCalculations.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-bookmark fa-2x"></i>
                  <p>No saved calculations yet</p>
                </div>
              ) : (
                savedCalculations.map(calc => (
                  <div key={calc.id} className="saved-item" onClick={() => loadCalculation(calc)}>
                    <div className="saved-name">{calc.name}</div>
                    <div className="saved-details">
                      <span className={`profit ${calc.result.netProfit >= 0 ? 'positive' : 'negative'}`}>
                        ${calc.result.netProfit.toFixed(2)}
                      </span>
                      <span className="date">{calc.timestamp}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'Calculator' : 'Profit Calculator'}
              </button>
              <button 
                className={`tab ${activeTab === "projection" ? "active" : ""}`}
                onClick={() => setActiveTab("projection")}
              >
                <i className="fas fa-chart-line"></i>
                {isMobile ? 'Projection' : 'Investment Projection'}
              </button>
              <button 
                className={`tab ${activeTab === "risk" ? "active" : ""}`}
                onClick={() => setActiveTab("risk")}
              >
                <i className="fas fa-shield-alt"></i>
                {isMobile ? 'Risk' : 'Risk Management'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Quick Presets */}
          <div className="presets-section">
            <h4>Quick Presets:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('conservative')}>
                Conservative
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('moderate')}>
                Moderate
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('aggressive')}>
                Aggressive
              </button>
            </div>
          </div>

          {/* Mode Selection */}
          <div className="mode-selection">
            <div className="mode-buttons">
              <button 
                className={`mode-btn ${mode === "trader" ? "active" : ""}`}
                onClick={() => setMode("trader")}
              >
                <i className="fas fa-bolt"></i>
                Trader Mode
              </button>
              <button 
                className={`mode-btn ${mode === "investor" ? "active" : ""}`}
                onClick={() => setMode("investor")}
              >
                <i className="fas fa-seedling"></i>
                Investor Mode
              </button>
            </div>
            <div className="mode-description">
              {mode === "trader" 
                ? "Short-term trading with leverage and real-time calculations"
                : "Long-term investing with dividends and growth projections"
              }
            </div>
          </div>

          {/* Calculator Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  <h3>
                    <i className="fas fa-chart-bar"></i>
                    Trade Details
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Buy Price</label>
                      <input 
                        type="number" 
                        name="buyPrice" 
                        value={inputs.buyPrice} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-layer-group"></i> Shares</label>
                      <input 
                        type="number" 
                        name="shares" 
                        value={inputs.shares} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Sell Price</label>
                      <input 
                        type="number" 
                        name="sellPrice" 
                        value={inputs.sellPrice} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-coins"></i> Buy Fee</label>
                      <input 
                        type="number" 
                        name="buyFee" 
                        value={inputs.buyFee} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-coins"></i> Sell Fee</label>
                      <input 
                        type="number" 
                        name="sellFee" 
                        value={inputs.sellFee} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-file-invoice-dollar"></i> Taxes</label>
                      <input 
                        type="number" 
                        name="taxes" 
                        value={inputs.taxes} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                  </div>

                  {/* Advanced Inputs */}
                  <h3>
                    <i className="fas fa-sliders-h"></i>
                    Advanced Settings
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-rocket"></i> Leverage</label>
                      <select name="leverage" value={inputs.leverage} onChange={handleChange}>
                        <option value="1">1x (No Leverage)</option>
                        <option value="2">2x</option>
                        <option value="3">3x</option>
                        <option value="5">5x</option>
                        <option value="10">10x</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrows-repeat"></i> Currency Rate</label>
                      <input 
                        type="number" 
                        name="currency" 
                        value={inputs.currency} 
                        onChange={handleChange}
                        placeholder="1.00"
                        step="0.01"
                      />
                    </div>
                    {mode === "investor" && (
                      <div className="input-field">
                        <label><i className="fa-solid fa-money-bill-wave"></i> Dividend per Share</label>
                        <input 
                          type="number" 
                          name="dividend" 
                          value={inputs.dividend} 
                          onChange={handleChange}
                          placeholder="0.00"
                          step="0.01"
                        />
                      </div>
                    )}
                  </div>

                  {mode === "investor" && (
                    <div className="input-row">
                      <div className="input-field">
                        <label><i className="fa-solid fa-calendar"></i> Dividend Frequency</label>
                        <select name="dividendFrequency" value={inputs.dividendFrequency} onChange={handleChange}>
                          <option value="monthly">Monthly</option>
                          <option value="quarterly">Quarterly</option>
                          <option value="semi-annual">Semi-Annual</option>
                          <option value="annual">Annual</option>
                        </select>
                      </div>
                      <div className="input-field">
                        <label><i className="fa-solid fa-dollar-sign"></i> Other Fees</label>
                        <input 
                          type="number" 
                          name="otherFees" 
                          value={inputs.otherFees} 
                          onChange={handleChange}
                          placeholder="0.00"
                          step="0.01"
                        />
                      </div>
                      <div className="input-field">
                        <label><i className="fa-solid fa-arrows-rotate"></i> Reinvestment</label>
                        <input 
                          type="number" 
                          name="reinvest" 
                          value={inputs.reinvest} 
                          onChange={handleChange}
                          placeholder="0.00"
                          step="0.01"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save to History
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Projection Tab */}
          {activeTab === "projection" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-project-diagram"></i>
                  Investment Projection
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-seedling"></i> Initial Investment</label>
                    <input 
                      type="number" 
                      name="initialInvestment" 
                      value={inputs.initialInvestment} 
                      onChange={handleChange}
                      placeholder="1000"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-calendar"></i> Monthly Contribution</label>
                    <input 
                      type="number" 
                      name="monthlyContribution" 
                      value={inputs.monthlyContribution} 
                      onChange={handleChange}
                      placeholder="100"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-clock"></i> Time Period (Months)</label>
                    <input 
                      type="number" 
                      name="timePeriod" 
                      value={inputs.timePeriod} 
                      onChange={handleChange}
                      placeholder="12"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-chart-line"></i> Exp. Annual Return (%)</label>
                    <input 
                      type="number" 
                      name="annualReturn" 
                      value={inputs.annualReturn} 
                      onChange={handleChange}
                      placeholder="8"
                      step="0.1"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Risk Management Tab */}
          {activeTab === "risk" && (
            <div className="risk-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-shield-alt"></i>
                  Risk Management
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-exclamation-triangle"></i> Risk Amount ($)</label>
                    <input 
                      type="number" 
                      name="riskAmount" 
                      value={inputs.riskAmount} 
                      onChange={handleChange}
                      placeholder="100"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-arrow-down"></i> Stop Loss Price</label>
                    <input 
                      type="number" 
                      name="stopLoss" 
                      value={inputs.stopLoss} 
                      onChange={handleChange}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-arrow-up"></i> Take Profit Price</label>
                    <input 
                      type="number" 
                      name="takeProfit" 
                      value={inputs.takeProfit} 
                      onChange={handleChange}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                </div><br/>

                {riskMetrics && (
                  <div className="risk-results">
                    <h4>Position Sizing</h4>
                    <div className="risk-metrics">
                      <div className="risk-metric">
                        <span className="metric-label">Shares to Buy:</span>
                        <span className="metric-value">{riskMetrics.positionSize}</span>
                      </div>
                      <div className="risk-metric">
                        <span className="metric-label">Total Investment:</span>
                        <span className="metric-value">${riskMetrics.totalInvestment.toFixed(2)}</span>
                      </div>
                      <div className="risk-metric">
                        <span className="metric-label">Risk Percentage:</span>
                        <span className="metric-value">{riskMetrics.riskPercentage}%</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Recent Calculations
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No calculations yet. Start calculating to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className={`history-profit ${calc.netProfit >= 0 ? 'positive' : 'negative'}`}>
                          <i className={`fas ${calc.netProfit >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'}`}></i>
                          ${calc.netProfit.toFixed(2)}
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-chart-line"></i> ROI: {calc.roi.toFixed(2)}%</span>
                        <span><i className="fas fa-percentage"></i> Gain: {calc.percentGain.toFixed(2)}%</span>
                        <span><i className="fas fa-balance-scale"></i> Break-even: ${calc.breakEvenPrice}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "projection" || activeTab === "risk") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-chart-bar"></i>
                    Profit Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {result ? (
                  <>
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className={`result-value ${result.netProfit >= 0 ? 'positive' : 'negative'}`}>
                          ${result.netProfit.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-money-bill-wave"></i>
                          Net Profit/Loss
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.percentGain.toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-percentage"></i>
                          Percentage Gain
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.roi.toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-chart-line"></i>
                          ROI
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.riskRewardRatio}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-balance-scale"></i>
                          Risk/Reward
                        </div>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-piggy-bank"></i> Total Investment:</span>
                        <span>${result.totalInvestment.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-receipt"></i> Total Cost (with fees):</span>
                        <span>${result.totalBuyCost.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-cash-register"></i> Total Sale Value:</span>
                        <span>${result.totalSellValue.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-coins"></i> Net Dividend Income:</span>
                        <span>${result.netDividend.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-rocket"></i> Leverage Used:</span>
                        <span>{result.leverageUsed}x</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-arrow-down"></i> Break-even Price:</span>
                        <span>${result.breakEvenPrice}</span>
                      </div>
                    </div>

                    {/* Risk Metrics */}
                    <div className="risk-summary">
                      <h4>Risk Assessment</h4>
                      <div className="risk-cards">
                        <div className="risk-card">
                          <div className="risk-value negative">
                            ${result.potentialLoss.toFixed(2)}
                          </div>
                          <div className="risk-label">Potential Loss</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value positive">
                            ${result.potentialGain.toFixed(2)}
                          </div>
                          <div className="risk-label">Potential Gain</div>
                        </div>
                      </div>
                    </div>

                    {/* Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Profit Distribution
                      </h3>
                      <Doughnut 
                        data={profitChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `$${context.parsed.toFixed(2)}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-calculator fa-3x"></i>
                    <p>Enter your trade details to see calculations</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "projection" && projectionData && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-chart-line"></i>
                  Growth Projection
                </h3>
                <div className="chart-container">
                  <Line 
                    data={projectionChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          display: true,
                          position: 'top'
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            callback: function(value) {
                              return '$' + value.toLocaleString();
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>
                <div className="projection-summary">
                  <div className="summary-card">
                    <div className="summary-value">
                      ${projectionData[projectionData.length - 1]?.value.toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-flag-checkered"></i>
                      Final Value
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      {((projectionData[projectionData.length - 1]?.value / parseFloat(inputs.initialInvestment) - 1) * 100).toFixed(1)}%
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-chart-line"></i>
                      Total Growth
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      {projectionData.length - 1}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-calendar"></i>
                      Total Months
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      ${(parseFloat(inputs.monthlyContribution) * (projectionData.length - 1)).toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-hand-holding-usd"></i>
                      Total Contributions
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "risk" && riskMetrics && (
              <div className="risk-results-panel">
                <h3>
                  <i className="fas fa-analytics"></i>
                  Risk Analysis
                </h3>
                <div className="risk-visualization">
                  <div className="risk-meter">
                    <div className="meter-label">Risk Level</div>
                    <div className="meter-bar">
                      <div 
                        className="meter-fill"
                        style={{ width: `${Math.min(riskMetrics.riskPercentage, 100)}%` }}
                      ></div>
                    </div>
                    <div className="meter-value">{riskMetrics.riskPercentage}%</div>
                  </div>
                  
                  <div className="position-breakdown">
                    <h4>Position Breakdown</h4>
                    <div className="breakdown-item">
                      <span>Risk per Share:</span>
                      <span>${riskMetrics.riskPerShare.toFixed(2)}</span>
                    </div>
                    <div className="breakdown-item">
                      <span>Position Size:</span>
                      <span>{riskMetrics.positionSize} shares</span>
                    </div>
                    <div className="breakdown-item">
                      <span>Total Investment:</span>
                      <span>${riskMetrics.totalInvestment.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default StockProfitCalculator;